/**
 * `shell/UserPill.tsx` — the user-pill dropdown + its static action list.
 *
 * Extracted from `AppShell.tsx` (behaviour-preserving). `<UserPill/>` is
 * the account/persona dropdown in the top-nav right cluster; the demo
 * persona/scenario switcher is gated to the `/demo` flow.
 * `<UserMenuActionList/>` is the static action group at the foot of the
 * dropdown (exported so vitest can exercise the rows directly).
 *
 * #3005 — the head shows the account's name and email. Under it, the
 * **Switch accounts** group (`<UserMenuAccountGroup/>`): one row per other
 * account signed in on this browser, then **Add another account**. Two
 * clicks each way: open the menu, pick the account. With two or more
 * accounts the sign-out rows read **Sign out of ‹email›** and **Sign out
 * of all accounts**. No group in a demo or in Telegram mode.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  Download,
  ExternalLink,
  History,
  Layers,
  LogOut,
  Palette,
  RefreshCw,
  Send,
  Settings,
  Shield,
  Trash2,
  User as UserIcon,
  Users as UsersIcon,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { apiFetch } from "../../api/client";
import { useTelegramLink } from "../../api/useTelegramLink";
import { useAuth } from "../../lib/AuthContext";
import {
  ADD_ACCOUNT_PATH,
  type SignOutScope,
  type SignedInAccount,
  signOutAndLeave,
} from "../../lib/accountSwitch";
import { isDemoPath, setDevAccountId } from "../../lib/auth";
import { cn } from "../../lib/cn";
import {
  type DevScenario,
  type DevScenarioResetResponse,
  getActiveScenario,
  isDemoMode,
  setActiveScenario,
} from "../../lib/demo";
import { leaveDemo } from "../../lib/demoSession";
import { toggleTheme } from "../../lib/theme";
import { DeleteAccountDialog } from "../DeleteAccountDialog";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui";
import { AccountList } from "./AccountList";
import { ModuleMenuItems } from "./ModuleSwitcher";

export function UserPill({
  showModules = false,
}: {
  /** #2048 — list the module previews in this menu (the top-bar module
   *  switcher is hidden for this account). */
  showModules?: boolean;
} = {}) {
  const { account, signOut, mode, accounts, switchAccount } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Issue 020 — the persona/scenario switcher is `/demo`-flow chrome,
  // not global chrome. Brief 36 (`aca1564`) gated `<LoadTemplateDialog/>`
  // on `team.is_demo` for the Setup pages, but the user-pill switcher
  // here was still rendering on every authenticated surface whenever
  // `isDemoMode()` returned true.
  //
  // `isDemoMode()` returns true on prod (host matches `*.pages.dev` /
  // `*.fly.dev`) AND on any session that has touched `/demo` once
  // (sticky `sessionStorage` flag) — meaning a real Pro account on the
  // founder's prod SPA opened the user-pill on Home / Team page / Setup
  // and saw demo personas (Dr Hart, Sister Khan, …) plus the demo
  // scenario list (Cardiology — Tarii reference, Theatre nursing —
  // skill-mix discipline, …) inside their own account chrome. That's
  // the exact regression the founder reported as issue 020.
  //
  // The fix mirrors the canonical `/demo*` path-gate Brief 27 + Brief 38
  // codified for the AuthProvider: render the switcher block only when
  // the active route is the demo flow. Reading the path via
  // `useLocation()` so the gate re-evaluates on in-app router
  // navigation (a hard reload is no longer required to flip it).
  const demoOn = isDemoMode();
  const onDemoPath = isDemoPath(location.pathname);
  const showDemoSwitcher = demoOn && onDemoPath;

  // Pull scenario list only when the switcher would actually render —
  // off-`/demo` surfaces shouldn't trigger an `/api/dev/scenarios` GET
  // for a payload that isn't going to surface anywhere.
  const scenariosQuery = useQuery({
    queryKey: ["dev", "scenarios"],
    queryFn: () => apiFetch<DevScenario[]>("/api/dev/scenarios"),
    enabled: showDemoSwitcher,
    staleTime: 30_000,
  });

  const activeScenarioName = getActiveScenario();
  const scenarios = scenariosQuery.data ?? [];
  const activeScenario =
    scenarios.find((s) => s.name === activeScenarioName) ?? scenarios[0] ?? null;

  // Demo persona/scenario switches do a hard reload (not router-navigate)
  // so `AuthProvider` re-evaluates which provider to mount based on the
  // freshly-set dev-shim id. Without the reload, Clerk's session keeps
  // intercepting API calls and the swap silently no-ops.
  const switchPersona = useCallback((accountIdToSwap: string) => {
    setDevAccountId(accountIdToSwap);
    window.location.assign("/");
  }, []);

  const switchScenario = useCallback((scenario: DevScenario) => {
    setActiveScenario(scenario.name);
    // Re-target to the lead persona on the new team.
    setDevAccountId(scenario.account_id);
    window.location.assign("/");
  }, []);

  const resetDemo = useCallback(async () => {
    if (!activeScenario) return;
    try {
      const result = await apiFetch<DevScenarioResetResponse>(
        `/api/dev/scenarios/${activeScenario.name}/reset`,
        { method: "POST" },
      );
      window.alert(`Reset ${result.team_name}: ${result.members_loaded} personas loaded.`);
      void queryClient.invalidateQueries();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Reset failed";
      window.alert(`Couldn't reset demo: ${message}`);
    }
  }, [activeScenario, queryClient]);

  const label = account?.display_name || account?.email || "Signed in";
  const initials = (account?.display_name || account?.email || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // #2095 — an admin account (`ADMIN_ACCOUNT_IDS`) gets a menu entry to
  // `/settings/admin`. Same gate as the switcher's *All teams (observer)*
  // section; read off the account already in context, so no new request.
  const isAdmin = account?.is_admin === true;

  // #3005 — the accounts signed in on this browser; none in a demo or Telegram.
  const showAccounts = accounts !== undefined && mode !== "demo" && mode !== "telegram";
  const severalAccounts = showAccounts && (accounts?.length ?? 0) > 1;
  const leave = (scope: SignOutScope) => {
    if (mode === "demo") {
      // #2779 — a demo visitor has no account: the action ends the demo.
      leaveDemo();
      return Promise.resolve();
    }
    // Clears any lingering dev-shim id, AWAITS the revoke (#1520), then
    // loads where the sign-out lands in full.
    return signOutAndLeave(signOut, accounts, scope);
  };

  // #2071 — a Telegram entry (→ `/connect/telegram`) when any of the
  // account's teams has the Telegram component on: the same read the phone
  // Profile sheet gates its row on (404 `telegram_off` → no entry).
  const telegramOn = useTelegramLink().available === true;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* `data-testid` is the E2E hook for the sign-out regression
            (#1520). The visible label is the account name, so there is no
            stable accessible name to locate this trigger by. */}
        <Button
          variant="secondary"
          size="sm"
          className="gap-2 pl-1.5 pr-2"
          data-testid="user-menu-trigger"
        >
          <span
            aria-hidden="true"
            className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent-fg"
          >
            {initials || "?"}
          </span>
          {/* #2660 — below `sm` the pill shows the initials only; the name stays
              for screen readers. */}
          <span className="sr-only sm:hidden">{label}</span>
          <span className="hidden max-w-[10rem] truncate text-sm sm:inline">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 text-fg-subtle" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          {account?.display_name ? (
            <span className="min-w-0 truncate" data-testid="user-menu-name">
              {account.display_name}
            </span>
          ) : null}
          <span
            className={cn(
              "min-w-0 truncate",
              account?.display_name && "text-xs font-normal text-fg-subtle",
            )}
          >
            {account?.email || "—"}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {showAccounts && accounts ? (
          <UserMenuAccountGroup
            accounts={accounts}
            onSwitch={(key) => void switchAccount?.(key)}
            onAdd={() => navigate(ADD_ACCOUNT_PATH)}
          />
        ) : null}

        {showDemoSwitcher && activeScenario ? (
          <>
            <DropdownMenuLabel>
              <span className="text-[10px] uppercase tracking-wider text-fg-subtle">
                Persona — {activeScenario.label}
              </span>
            </DropdownMenuLabel>
            {activeScenario.members_summary.length > 0 ? (
              activeScenario.members_summary.slice(0, 6).map((m, idx) => {
                // Members surfaced by `/api/dev/scenarios` are summary
                // rows — they don't carry account ids. We only know the
                // lead's account_id (the scenario.account_id). To swap
                // to a non-lead persona the operator clicks Dev debug
                // → manual id entry. For now, render the persona names
                // for transparency + offer a "back to lead" item.
                const isLead = idx === 0;
                return (
                  <DropdownMenuItem
                    key={`${activeScenario.name}-${m.display_name}`}
                    onSelect={() => {
                      if (isLead) switchPersona(activeScenario.account_id);
                    }}
                    disabled={!isLead}
                  >
                    <UserIcon className="h-4 w-4" aria-hidden="true" />
                    <span className="flex-1 truncate">{m.display_name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-fg-subtle">
                      {isLead ? "lead" : m.role}
                    </span>
                  </DropdownMenuItem>
                );
              })
            ) : (
              <DropdownMenuItem disabled>
                <UsersIcon className="h-4 w-4" aria-hidden="true" />
                <span className="text-fg-subtle">No personas loaded</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>
              <span className="text-[10px] uppercase tracking-wider text-fg-subtle">Scenario</span>
            </DropdownMenuLabel>
            {scenarios.map((s) => (
              <DropdownMenuItem
                key={s.name}
                onSelect={() => switchScenario(s)}
                className={cn(s.name === activeScenario.name ? "bg-bg-subtle" : "")}
              >
                <Layers className="h-4 w-4" aria-hidden="true" />
                <span className="flex-1 truncate">{s.label}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                void resetDemo();
              }}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Reset demo
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}

        {/* #2048 — the module previews, when the top-bar switcher is hidden. */}
        {showModules ? (
          <>
            <DropdownMenuLabel>
              <span className="text-[10px] uppercase tracking-wider text-fg-subtle">
                Other modules
              </span>
            </DropdownMenuLabel>
            <ModuleMenuItems exclude="Roster" />
            <DropdownMenuSeparator />
          </>
        ) : null}

        <UserMenuActionList
          onProfileSettings={() => navigate("/settings/profile")}
          onTariiAdmin={isAdmin ? () => navigate("/settings/admin") : undefined}
          onTelegram={telegramOn ? () => navigate("/connect/telegram") : undefined}
          onActivity={() => navigate("/me/audit")}
          onExportData={() => navigate("/me/export")}
          onNextTheme={() => toggleTheme()}
          onDevDebug={() => navigate("/dev")}
          // #2779 — a demo visitor has no account: the same action ends the
          // demo and lands on the website.
          signOutLabel={
            mode === "demo"
              ? "Back to the website"
              : severalAccounts
                ? `Sign out of ${account?.email || "this account"}`
                : undefined
          }
          // #3005 — `signOutAndLeave`: clear the dev-shim id (so a Clerk
          // sign-out never re-mounts the demo provider), AWAIT the revoke
          // (#1520: navigating in the same tick aborted it), then a full
          // load, so AuthProvider re-evaluates which provider to mount.
          onSignOut={() => void leave("current")}
          onSignOutAll={severalAccounts ? () => void leave("all") : undefined}
          onDeleteAccount={() => setDeleteOpen(true)}
        />
      </DropdownMenuContent>
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
    </DropdownMenu>
  );
}

/**
 * `<UserMenuActionList/>` — Brief 35 (Round-N Wave 2). The static
 * action items at the bottom of the user-pill dropdown.
 *
 * Extracted as an exported component so vitest can exercise the rows
 * directly without driving Radix's pointer-event `DropdownMenu` open —
 * mirrors the `<WorkspaceMenuList/>` precedent in
 * `web/src/components/WorkspaceSwitcher.tsx`. happy-dom doesn't
 * simulate Radix's pointer-event-driven open-on-click reliably, so
 * the load-bearing "Profile settings" link (closing the last loose end
 * of issue 001 — `/settings/profile` was direct-URL-only at v0.1) gets
 * covered by direct-render of this component instead.
 *
 * Order is intentional + brand-pillar-driven:
 *
 *   1. **Profile settings** → `/settings/profile`. Top of the static
 *      action group because the worker's identity (display_name,
 *      timezone, future notification preferences) is the most-personal
 *      surface in the menu. Issue 001 closure. (#1294 removed the
 *      former "Account & billing" item along with the Billing surface.)
 *      **Telegram** → `/connect/telegram` follows it, rendered only
 *      when the caller passes `onTelegram` (a team has Telegram on, #2071).
 *      **Tarii admin** → `/settings/admin` follows it, rendered only
 *      when the caller passes `onTariiAdmin` (admin accounts, #2095).
 *   2. **Activity** → `/me/audit` — the account-scoped audit trail
 *      (who changed what on my behalf). #1645 gave it a permanent
 *      account-menu entry: it's an account surface as much as a nav
 *      one, and the account menu is where it stays reachable when the
 *      member nav is reworked. Ungated — every role has an audit
 *      trail.
 *   3. **Export your data** → `/me/export` — the GDPR Art. 15/20 +
 *      PDPA data-subject-access surface (#153). A rights surface, not
 *      a feature: never gated, and a persistent menu entry because a
 *      data-access right you can't find is a compliance smell (#1645
 *      — previously direct-URL-only on desktop; the phone More-sheet
 *      entry stays).
 *   4. **Next theme** — chrome polish; supporting affordance.
 *   5. **Dev debug** — operator-only escape hatch.
 *   6. **Sign out** — terminal session action; sits below the
 *      navigational items per common UX convention.
 *   7. **Delete my account** — destructive; isolated by separator + red
 *      tone so it never reads as a casual click target.
 *   8. **API docs** — external link; isolated at the foot.
 *
 * Focal-component discipline: the user-pill itself is the chrome
 * anchor (focal at the AppShell-section level); inside this dropdown
 * the items are uniform-weight supporting affordances — the dropdown
 * is an inventory surface (per `feedback_ui_section_focal_component.md`).
 * "Profile settings" is load-bearing for issue 001 but not visually
 * elevated — discoverability comes from being first in the static
 * action group, not from heavier visual weight.
 */
export interface UserMenuActionListProps {
  onProfileSettings: () => void;
  /** #2095 — admin accounts only. When set, a *Tarii admin* entry
   *  (→ `/settings/admin`) renders right after *Profile settings*. */
  onTariiAdmin?: (() => void) | undefined;
  /** #2071 — accounts with Telegram on for a team. When set, a *Telegram*
   *  entry (→ `/connect/telegram`) renders right after *Profile settings*. */
  onTelegram?: (() => void) | undefined;
  onActivity: () => void;
  onExportData: () => void;
  onNextTheme: () => void;
  onDevDebug: () => void;
  onSignOut: () => void;
  /** #2779 — the sign-out row's label; a demo session passes
   *  "Back to the website". Defaults to "Sign out". #3005 — with two or
   *  more accounts signed in: "Sign out of ‹email›". */
  signOutLabel?: string | undefined;
  /** #3005 — two or more accounts signed in: a **Sign out of all
   *  accounts** row follows the sign-out row. */
  onSignOutAll?: (() => void) | undefined;
  onDeleteAccount: () => void;
}

export function UserMenuActionList({
  onProfileSettings,
  onTariiAdmin,
  onTelegram,
  onActivity,
  onExportData,
  onNextTheme,
  onDevDebug,
  onSignOut,
  signOutLabel = "Sign out",
  onSignOutAll,
  onDeleteAccount,
}: UserMenuActionListProps) {
  return (
    <>
      <DropdownMenuItem onSelect={() => onProfileSettings()}>
        <Settings className="h-4 w-4" aria-hidden="true" />
        Profile settings
      </DropdownMenuItem>
      {onTelegram ? (
        <DropdownMenuItem onSelect={() => onTelegram()} data-testid="user-menu-telegram">
          <Send className="h-4 w-4" aria-hidden="true" />
          Telegram
        </DropdownMenuItem>
      ) : null}
      {onTariiAdmin ? (
        <DropdownMenuItem onSelect={() => onTariiAdmin()}>
          <Shield className="h-4 w-4" aria-hidden="true" />
          Tarii admin
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem onSelect={() => onActivity()}>
        <History className="h-4 w-4" aria-hidden="true" />
        Activity
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={() => onExportData()}>
        <Download className="h-4 w-4" aria-hidden="true" />
        Export your data
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          onNextTheme();
        }}
      >
        <Palette className="h-4 w-4" aria-hidden="true" />
        Next theme
      </DropdownMenuItem>
      {/* #774 — the "Dev debug" item navigates to `/dev`, which renders
          `<AccountDebug/>` (a `Bearer dev:<uuid>` impersonation probe).
          That route is registered only under `import.meta.env.DEV`, so
          gate the menu trigger the same way — otherwise prod shows a dead
          menu item pointing at an unregistered route. */}
      {import.meta.env.DEV && (
        <DropdownMenuItem onSelect={() => onDevDebug()}>
          <UserIcon className="h-4 w-4" aria-hidden="true" />
          Dev debug
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onSelect={() => onSignOut()}>
        <LogOut className="h-4 w-4" aria-hidden="true" />
        <span className="min-w-0 truncate">{signOutLabel}</span>
      </DropdownMenuItem>
      {onSignOutAll ? (
        <DropdownMenuItem onSelect={() => onSignOutAll()} data-testid="user-menu-sign-out-all">
          <LogOut className="h-4 w-4" aria-hidden="true" />
          Sign out of all accounts
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          onDeleteAccount();
        }}
        className="text-danger data-[highlighted]:bg-danger-soft/40 data-[highlighted]:text-danger"
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
        Delete my account
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild>
        <a href="/api/docs" target="_blank" rel="noreferrer">
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
          API docs
        </a>
      </DropdownMenuItem>
    </>
  );
}

/**
 * `<UserMenuAccountGroup/>` — #3005, the **Switch accounts** group: one
 * row per OTHER account signed in on this browser (the head already names
 * the current one), then **Add another account**. Exported for direct
 * render in vitest, like `<UserMenuActionList/>`.
 */
export function UserMenuAccountGroup({
  accounts,
  onSwitch,
  onAdd,
}: {
  accounts: ReadonlyArray<SignedInAccount>;
  onSwitch: (key: string) => void;
  onAdd: () => void;
}) {
  return (
    <>
      <DropdownMenuLabel>
        <span className="text-[10px] uppercase tracking-wider text-fg-subtle">Switch accounts</span>
      </DropdownMenuLabel>
      <AccountList
        accounts={accounts.filter((a) => !a.current)}
        variant="menu"
        onPick={(a) => onSwitch(a.key)}
        onAdd={onAdd}
      />
      <DropdownMenuSeparator />
    </>
  );
}
